import { ChevronLeft, ChevronRight, ListIcon, Loader2, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table'
import { useEffect, useMemo, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, startOfMonth, startOfWeek } from 'date-fns';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from '../../../lib/axios'
import { useAuth } from '../../../context/AuthProvider';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import AddActivity from './(components)/add-activity';
import { Trans, useLingui } from '@lingui/react/macro';
import { AxiosError } from 'axios';
import EditActivity from './(components)/edit-activity';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../../components/ui/alert-dialog';

const CalendarHomepage = () => {
    const { auth } = useAuth()
    const { t } = useLingui();

    const dbid = auth.data?.databases[0].dbid;
    const technicianid = auth.data?.databases[0].technicianid;

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const monthStartKey = format(startOfMonth(currentMonth), 'yyyyMMdd');
    const monthEndKey = format(endOfMonth(currentMonth), 'yyyyMMdd');

    const { data: overview, isError, refetch: refetchOverview } = useQuery({
        queryKey: [`overview-${selectedDate.getDay()}`],
        queryFn: async () => {
            toast.loading(t`Overzicht wordt geladen...`, { id: 'calendar-overview' })
            console.log("Loading", format(selectedDate, "yyyyMMdd"))
            const { data } = await axios.get(`overview/month`, {
                params: {
                    dbid: auth.data?.databases[0].dbid,
                    technicianid: auth.data?.databases[0].technicianid,
                    startdate: format(selectedDate, "yyyyMMdd"),
                    enddate: format(selectedDate, "yyyyMMdd"),
                }
            })
            toast.dismiss('calendar-overview')
            return data
        },
    })

    const { mutate: deleteTriptime, isPending: isDeletingTriptime } = useMutation({
        mutationFn: async ({ triptimeid }: { triptimeid: string }) => {
            toast.loading(t`Activiteit wordt verwijderd...`, { id: 'calendar-delete' })
            await axios.post(`triptimedelete?dbid=${auth.data?.databases[0].dbid}&triptimeid=${triptimeid}`, {})
        },
        onSuccess: () => {
            toast.success(t`Activiteit verwijderd.`, { id: 'calendar-delete' })
            refetchOverview()
        },
        onError: (err) => {
            toast.error(t`Er is een fout opgetreden bij het verwijderen van de activiteit.`, { id: 'calendar-delete' })
            console.error(err)
        }
    })

    const { mutate: closeDay, isPending: isClosingDay } = useMutation({
        mutationFn: async () => {
            await axios.post(`overview/closeday?dbid=${auth.data?.databases[0].dbid}`, {
                date: format(selectedDate, "yyyyMMdd"),
                technicianid: auth.data?.databases[0].technicianid,
            })
        },
        onSuccess: () => {
            toast.success(t`Dag gesloten.`)
            refetchOverview()
        },
        onError: (err) => {
            if (err instanceof AxiosError) {
                toast.error(err.message)
            }
            else if (err instanceof Error) {
                toast.error(err.message)
            }
            else {
                toast.error(t`Er is een fout opgetreden bij het sluiten van de dag.`)
            }
        }
    })

    useEffect(() => {
        if (isError) toast.error(t`Er is een fout opgetreden bij het ophalen van de overzicht.`, { id: 'calendar-overview' })
    }, [isError])

    const handlePrevMonth = () => {
        setCurrentMonth((prev) => addMonths(prev, -1));
    };

    const handleNextMonth = () => {
        setCurrentMonth((prev) => addMonths(prev, 1));
    };

    // Get days for the current month view
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const weekStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday as start of the week
    const weekEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    const { data: monthDays } = useQuery({
        queryKey: ['overview-days', dbid, technicianid, monthStartKey, monthEndKey],
        queryFn: async ({ signal }) => {
            const { data } = await axios.get('overview/days', {
                signal,
                params: { dbid, technicianid, startdate: monthStartKey, enddate: monthEndKey },
            });
            return data as { days: { DATE: string; DAYCLOSED: boolean }[] };
        },
        enabled: !!dbid && !!technicianid,
        staleTime: 30_000,
    });
    const workedMap = useMemo(() => {
        const m = new Map<string, 'closed' | 'open'>();
        for (const d of monthDays?.days ?? []) {
            m.set(d.DATE, d.DAYCLOSED ? 'closed' : 'open');
        }
        return m;
    }, [monthDays]);

    return (
        <div className='flex flex-col gap-4 p-2 sm:p-4'>
            <div>
                <Link to="month"><Button variant='outline'><ListIcon className='w-4 h-4' /><Trans>Open maandoverzicht</Trans></Button></Link>
            </div>

            <div className="space-y-3">
                <div className="grid grid-cols-[40px,1fr,40px]">
                    <div className="flex items-center justify-start"><button onClick={handlePrevMonth} className="flex items-center justify-center w-6 h-6 border rounded-full text-neutral-400 hover:text-neutral-600"><ChevronLeft className="w-4 h-4" /></button></div>
                    <div className="flex items-center justify-center"><p className="text-sm font-medium">{format(currentMonth, "MMMM yyyy")}</p></div>
                    <div className="flex items-center justify-end"><button onClick={handleNextMonth} className="flex items-center justify-center w-6 h-6 border rounded-full text-neutral-400 hover:text-neutral-600"><ChevronRight className="w-4 h-4" /></button></div>
                </div>
                <div className="grid grid-cols-7">
                    {["Maa", "Din", "Woe", "Don", "Vri", "Zat", "Zon"].map((day, index) => (
                        <div key={`day-${index}`} className="text-xs text-center text-neutral-500">{day}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 border bg-neutral-50 rounded-xl">
                    {days.map((day, index) => {
                        const dayKey = format(day, 'yyyyMMdd');
                        const isSelected = isSameDay(selectedDate, day);
                        const faded = !isSameMonth(day, currentMonth);
                        const status = workedMap.get(dayKey);

                        const statusClass = !isSelected
                            ? status === 'closed'
                                ? "bg-primary/15 text-primary ring-1 ring-primary/20"
                                : status === 'open'
                                    ? "bg-destructive/15 text-destructive ring-1 ring-destructive/20"
                                    : ""
                            : "";

                        return (
                            <button
                                type="button"
                                key={index}
                                onClick={() => setSelectedDate(day)}
                                className={[
                                    "flex items-center justify-center w-full h-12 rounded-lg aspect-square cursor-pointer transition",
                                    isSelected ? "bg-primary text-primary-foreground" : "hover:bg-primary/20",
                                    faded ? "text-neutral-300" : "text-neutral-600",
                                    statusClass,
                                ].join(" ")}
                                aria-label={dayKey}
                                data-status={status ?? 'none'}
                            >
                                <p className="text-sm font-medium">{format(day, "d")}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className='flex items-center justify-between'>
                <AddActivity
                    onSuccess={() => { refetchOverview() }}
                    date={selectedDate}
                    trigger={
                        <Button><PlusIcon className='w-4 h-4' /><Trans>Nieuwe activiteit</Trans></Button>
                    }
                />
                {overview && <p className='text-sm'><Trans>Totaal aantal uur: {overview?.totalhours}</Trans></p>}
            </div>

            <div className='overflow-auto border rounded-md'>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead><Trans>Locatie / Activiteit</Trans></TableHead>
                            <TableHead><Trans>Start uur</Trans></TableHead>
                            <TableHead><Trans>Eind uur</Trans></TableHead>
                            <TableHead></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(!overview || !overview?.days || (Array.isArray(overview?.days) && overview.days.length === 0)) && <TableRow><TableCell colSpan={3}><Trans>Geen activiteiten gevonden</Trans></TableCell></TableRow>}
                        {Array.isArray(overview?.days) &&
                            (!Array.isArray(overview.days[0]?.times) || overview.days[0].times.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Trans>Geen activiteiten gevonden</Trans>
                                    </TableCell>
                                </TableRow>
                            )}

                        {Array.isArray(overview?.days) && Array.isArray(overview.days[0]?.times) && overview.days[0].times.map((entry: any, index: number) => {
                            const isWork = entry.TIMEREGISTRATIONID > 0;
                            const isTrip = entry.TRIPTIMEID > 0;
                            const canDelete = entry.bDELETE && isTrip;
                            const starttime = entry.STARTTIME?.substring(0, 5); // "08:30:00.000" → "08:30"
                            const endtime = entry.ENDTIME?.substring(0, 5);

                            return (
                                <TableRow key={`entry-${index}`}>
                                    <TableCell>{entry.LOCATION}</TableCell>
                                    <TableCell>{starttime}</TableCell>
                                    <TableCell>{endtime}</TableCell>
                                    <TableCell>
                                        {overview && !overview.dayclosed &&
                                            <div className="flex items-center justify-end">
                                                {canDelete && (
                                                    <button disabled={isDeletingTriptime} onClick={() => deleteTriptime({ triptimeid: entry.TRIPTIMEID })}>
                                                        <Trash2Icon className="w-4 h-4 text-red-600" />
                                                    </button>
                                                )}
                                                <EditActivity
                                                    date={selectedDate}
                                                    row={entry}
                                                    onSuccess={() => refetchOverview()}
                                                    trigger={<Button variant="ghost"> <PencilIcon className='w-4 h-4' /></Button>}
                                                />
                                            </div>
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            <div>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button variant='secondary' disabled={isClosingDay || overview?.dayclosed}>
                            Dag sluiten
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle><Trans>Wilt u deze dag definitief afsluiten?</Trans></AlertDialogTitle>
                            <AlertDialogDescription><Trans>U kan deze actie niet ongedaan maken</Trans></AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => { closeDay() }}>
                                {isClosingDay && <Loader2 className='w-4 h-4 animate-spin' />}
                                <Trans>Afsluiten</Trans>
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    )
}

export default CalendarHomepage